import { Form as ReactForm } from 'react-bootstrap';
import React, { RefObject } from 'react';
import { ErrorMessage, Field, FieldProps, GenericFieldHTMLAttributes } from 'formik';
import Textarea from 'react-textarea-autosize';
import './FormField.css';
import { Dropdown } from '@salvapp/ui-components';
import { clsx } from 'clsx';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';

export type OptionType = DropdownOption;

interface FormFieldProps {
  id?: string;
  name: string;
  label?: string;
  type?: string;
  text?: string;
  placeholder?: string;
  options?: OptionType[];
  isMulti?: boolean;
  rows?: number;
  minValue?: number;
  minRows?: number;
  disabled?: boolean;
  inputref?: RefObject<any>;
  onFieldChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  checked?: boolean;
  isInvalid?: boolean;
  formGroupClassName?: string;
  labelClassName?: string;
  showRequiredAsteriskForLabel?: boolean;
  preprocess?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => unknown;
}

const FormField = ({
  id,
  name,
  label,
  type,
  text,
  placeholder,
  options,
  isMulti,
  rows,
  minValue,
  minRows,
  disabled,
  inputref,
  onFieldChange,
  checked,
  isInvalid,
  preprocess,
  formGroupClassName,
  labelClassName,
  showRequiredAsteriskForLabel,
  ...other
}: FormFieldProps & GenericFieldHTMLAttributes) => {
  const fieldType: string = type || 'text';

  return (
    <Field id={id} name={name} type={fieldType} placeholder={placeholder} {...other}>
      {({ form: { errors, values, setFieldValue, handleChange }, field }: FieldProps) => {
        if (preprocess) {
          const onChange = field.onChange;
          field.onChange = (e: React.ChangeEvent<any>) => onChange(preprocess(e));
        }
        return (
          <ReactForm.Group controlId={id} className={clsx('form-group', formGroupClassName)}>
            {fieldType !== 'checkbox' && fieldType !== 'radio' && label && (
              <>
                <ReactForm.Label column={false} className={labelClassName}>
                  {label}
                  {showRequiredAsteriskForLabel && <span className='required-asterisk'>*</span>}
                </ReactForm.Label>
                {text && <ReactForm.Text muted>{text}</ReactForm.Text>}
              </>
            )}
            {fieldType === 'select' && options ? (
              <Dropdown
                id={id}
                name={field.name}
                placeholder={placeholder}
                options={options}
                value={field.value}
                onChange={(value: DropdownOption[]) => setFieldValue(field.name, isMulti ? value : value[0])}
                isMulti={isMulti}
                disabled={disabled}
              />
            ) : fieldType === 'textarea' ? (
              <ReactForm.Control
                {...field}
                as={Textarea}
                placeholder={placeholder}
                data-toggle={rows ? undefined : 'autosize'}
                rows={rows}
                minRows={minRows}
                disabled={disabled}
                isInvalid={!!errors[name]}
                ref={inputref}
                data-testid={name}
                aria-label={label}
              />
            ) : fieldType === 'checkbox' ? (
              <>
                <ReactForm.Check className='form-check form-switch'>
                  <ReactForm.Check.Input
                    {...field}
                    onChange={onFieldChange ?? handleChange}
                    type={fieldType}
                    id={id}
                    checked={checked ?? values[name]}
                    disabled={disabled}
                    className='form-check-input'
                    data-testid={name}
                    aria-invalid={isInvalid}
                  />
                  <ReactForm.Check.Label htmlFor={id} className='form-check-label'>
                    {label}
                  </ReactForm.Check.Label>
                </ReactForm.Check>
                {text && <ReactForm.Text muted>{text}</ReactForm.Text>}
              </>
            ) : fieldType === 'radio' ? (
              <>
                <ReactForm.Check>
                  <ReactForm.Check.Input
                    id={id}
                    name={name}
                    isInvalid={!!errors[name]}
                    onChange={handleChange}
                    type={fieldType}
                    value={id}
                    data-testid={name}
                  />
                  <ReactForm.Check.Label htmlFor={id}>{label}</ReactForm.Check.Label>
                </ReactForm.Check>
              </>
            ) : (
              <ReactForm.Control
                {...field}
                placeholder={placeholder}
                type={fieldType}
                min={minValue}
                disabled={disabled}
                isInvalid={isInvalid ?? !!errors[name]}
                className={disabled ? 'form-control-plaintext' : ''}
                data-testid={name}
                aria-label={label}
              />
            )}
            <ErrorMessage
              name={name}
              render={(message) => (
                <ReactForm.Control.Feedback data-testid={name + '-error'} type='invalid'>
                  {message}
                </ReactForm.Control.Feedback>
              )}
            />
          </ReactForm.Group>
        );
      }}
    </Field>
  );
};

FormField.displayName = 'FormField';

export default FormField;
